/**
 * Centralized event tracking system for PostHog analytics
 *
 * This file defines all custom events we track, with TypeScript types
 * to ensure consistency and prevent typos across the application.
 */

// Core conversion events - these are the most important for business metrics
export const CONVERSION_EVENTS = {
	USER_SIGNED_UP: "user_signed_up",
	EMAIL_VERIFIED: "email_verified",
	TRIAL_STARTED: "trial_started",
	PROPERTY_VERIFIED: "property_verified",
	PURCHASE_COMPLETED: "purchase_completed",
} as const;

// Marketing funnel events - track user journey through marketing site
export const MARKETING_EVENTS = {
	PRICING_CLICKED: "pricing_clicked",
	CONTACT_FORM_SUBMITTED: "contact_form_submitted",
	CTA_CLICKED: "cta_clicked",
} as const;

// Product usage events - track user behavior in the app
export const PRODUCT_EVENTS = {
	PURCHASE_INITIATED: "purchase_initiated",
	SUBSCRIPTION_CANCELLED: "subscription_cancelled",
	SUBSCRIPTION_RENEWED: "subscription_renewed",
} as const;

// Setup path events - how long a new property takes to start tracking
export const SETUP_EVENTS = {
	WORKFLOW_STARTED: "setup_workflow_started",
	TRACKING_READY: "setup_tracking_ready",
} as const;

// AI Report events - track AI visibility report flow
export const AI_REPORT_EVENTS = {
	REPORT_GENERATION_REQUESTED: "ai_report_generation_requested",
	REPORT_GENERATION_COMPLETED: "ai_report_generation_completed",
	REPORT_GENERATION_FAILED: "ai_report_generation_failed",
	REPORT_VIEWED: "ai_report_viewed",
	UNLOCK_BUTTON_CLICKED: "ai_report_unlock_button_clicked",
	REPORT_PURCHASE_INITIATED: "ai_report_purchase_initiated",
	REPORT_PURCHASED: "ai_report_purchased",
	REPORT_SHARED: "ai_report_shared",
	// Tour events
	TOUR_STARTED: "ai_report_tour_started",
	TOUR_SLIDE_VIEWED: "ai_report_tour_slide_viewed",
	TOUR_NAVIGATED: "ai_report_tour_navigated",
	TOUR_COMPLETED: "ai_report_tour_completed",
	TOUR_CLOSED: "ai_report_tour_closed",
	TOUR_CTA_CLICKED: "ai_report_tour_cta_clicked",
} as const;

// Free AI visibility prompt generator (/free-ai-visibility-prompt-generator).
// No event ever carries the visitor's email address: EMAIL_SUBMITTED is the
// generation id only. CLAIM_COMPLETED is fired by the signup claim step.
export const PROMPT_GENERATOR_EVENTS = {
	GENERATION_REQUESTED: "prompt_generator_requested",
	GENERATION_COMPLETED: "prompt_generator_completed",
	GENERATION_FAILED: "prompt_generator_failed",
	PERSONAS_VIEWED: "prompt_generator_personas_viewed",
	EMAIL_SUBMITTED: "prompt_generator_email_submitted",
	EXPORT_CLICKED: "prompt_generator_export_clicked",
	TRACK_CTA_CLICKED: "prompt_generator_track_cta_clicked",
	CLAIM_COMPLETED: "prompt_generator_claim_completed",
} as const;

// Go-to-market conversions, fired from the browser through `trackConversion`
// in apps/web/modules/analytics, which sends each one to PostHog and to the
// LinkedIn Insight Tag. Distinct from `user_signed_up`, `ai_report_purchased`
// and `purchase_completed`, which are sent server-side, so nothing is counted
// twice in PostHog.
export const GTM_CONVERSION_EVENTS = {
	GTM_REPORT_PREVIEW_RUN: "gtm_report_preview_run",
	GTM_REPORT_PURCHASED: "gtm_report_purchased",
	GTM_SIGNUP_COMPLETED: "gtm_signup_completed",
	GTM_DEMO_BOOKED: "gtm_demo_booked",
} as const;

export type GtmConversionName =
	(typeof GTM_CONVERSION_EVENTS)[keyof typeof GTM_CONVERSION_EVENTS];

// Event data interfaces for type safety
export interface UserSignedUpData {
	method: "email" | "google" | "github" | "magic_link";
	hasOrganization: boolean;
	organizationName?: string;
}

export interface EmailVerifiedData {
	userId: string;
	email: string;
	hasOrganization: boolean;
}

export interface TrialStartedData {
	planIntent?: string; // Plan user selected from pricing (if any)
	intervalIntent?: string; // Interval user selected (month/year)
	credits: number; // Number of trial credits granted
	trialDays: number; // Duration of trial in days
}

export interface PricingClickedData {
	source: "navbar" | "footer" | "pricing_section";
	location: string; // Current page path
}

export interface ContactFormSubmittedData {
	name: string;
	email: string;
	messageLength: number;
}

export interface CTAClickedData {
	plan: string;
	price?: number;
	currency?: string;
	location: "pricing_table" | "hero" | "other";
	isLoggedIn: boolean;
}

export interface PropertyVerifiedData {
	propertyId: string;
	apiKey: string;
	verificationMethod: "automatic" | "manual";
}

export interface PurchaseInitiatedData {
	plan: string;
	price: number;
	currency: string;
	propertyId?: string;
	organizationId?: string;
}

export interface PurchaseCompletedData {
	plan: string;
	price: number;
	currency: string;
	propertyId?: string;
	organizationId?: string;
	subscriptionId?: string;
	paymentMethod: "stripe";
}

/** The setup workflow was started for a property, by hand or on creation. */
export interface SetupWorkflowStartedData {
	propertyId: string;
	organizationId: string;
	subjectType: string;
	/** True when creating the property started it rather than a person did. */
	auto: boolean;
}

/**
 * A property reached tracking-ready: an active project with a goal of any
 * kind on it. Sent once per property, the first time a read notices it, which
 * is what makes `daysSinceCreated` the time-to-value clock.
 */
export interface SetupTrackingReadyData {
	propertyId: string;
	organizationId: string;
	subjectType: string;
	daysSinceCreated: number;
	/** False for a property set up without ever opening the setup workflow. */
	hadSetupRun: boolean;
}

export interface SubscriptionEventData {
	subscriptionId: string;
	plan: string;
	propertyId?: string;
	organizationId?: string;
}

// AI Report event data interfaces
export interface AIReportGenerationRequestedData {
	domain: string;
	reportId: string;
	propertyId: string;
	userEmail?: string;
	source: "marketing_page" | "dashboard" | "api";
}

export interface AIReportPageViewedData {
	location: string;
	isLoggedIn: boolean;
}

export interface AIReportGenerationCompletedData {
	domain: string;
	reportId: string;
	propertyId: string;
	consistencyScore: number;
	generationTimeMs: number;
	modelsUsed: string[];
	pagesAnalyzed: number;
}

export interface AIReportGenerationFailedData {
	domain: string;
	reportId: string;
	propertyId: string;
	errorType:
		| "domain_validation"
		| "crawling_failed"
		| "llm_error"
		| "timeout"
		| "robots_txt_blocked"
		| "insufficient_content"
		| "cloudflare_blocked"
		| "noscript_only"
		| "other";
	errorMessage: string;
	docsUrl?: string;
	// Additional context fields for specific error types
	spyglassesBlocked?: boolean;
	wildcardDisallow?: boolean;
	affectedPages?: number;
	totalPages?: number;
	// Robots.txt analysis fields
	robotsTxtStatus?: "red" | "yellow" | "green";
	blockedAiVisitors?: string[];
	blockedAiModelTrainers?: string[];
}

export interface AIReportViewedData {
	reportId: string;
	domain: string;
	consistencyScore?: number;
	isComplete: boolean;
	isPurchased: boolean;
	viewSource: "direct_link" | "demo_report" | "dashboard";
}

export interface AIReportUnlockButtonClickedData {
	reportId: string;
	domain: string;
	section:
		| "unlock_status"
		| "ai_visibility"
		| "recommendations"
		| "brand_snapshot"
		| "llm_analysis";
	consistencyScore?: number;
}

export interface AIReportPurchaseInitiatedData {
	reportId: string;
	domain: string;
	consistencyScore: number;
	price: number;
	currency: string;
	userEmail?: string;
}

export interface AIReportPurchasedData {
	reportId: string;
	domain: string;
	consistencyScore?: number;
	price?: number;
	currency?: string;
	userEmail?: string;
	paymentMethod: "stripe";
}

export interface AIReportSharedData {
	reportId: string;
	domain: string;
	shareMethod: "link_copy" | "email" | "social";
	isPurchased: boolean;
}

// Tour event data interfaces
export interface AIReportTourStartedData {
	reportId: string;
	domain: string;
	isUnlocked: boolean;
	totalSlides: number;
	trigger: "auto" | "button_click";
	hasAgencyBranding: boolean;
}

export interface AIReportTourSlideViewedData {
	reportId: string;
	domain: string;
	isUnlocked: boolean;
	slideId: string;
	slideIndex: number;
	totalSlides: number;
	progress: number; // 0-100
}

export interface AIReportTourNavigatedData {
	reportId: string;
	domain: string;
	isUnlocked: boolean;
	direction: "next" | "previous" | "skip";
	fromSlideId: string;
	fromSlideIndex: number;
	toSlideIndex: number;
	totalSlides: number;
}

export interface AIReportTourCompletedData {
	reportId: string;
	domain: string;
	isUnlocked: boolean;
	totalSlides: number;
	slidesViewed: number;
}

export interface AIReportTourClosedData {
	reportId: string;
	domain: string;
	isUnlocked: boolean;
	closedAtSlideId: string;
	closedAtSlideIndex: number;
	totalSlides: number;
	progress: number; // 0-100
	closeMethod: "button" | "escape" | "backdrop" | "cta";
}

export interface AIReportTourCTAClickedData {
	reportId: string;
	domain: string;
	isUnlocked: boolean;
	ctaType:
		| "unlock"
		| "explore_report"
		| "explore_preview"
		| "schedule_consultation";
	slideId: string;
	slideIndex: number;
}

// Prompt generator event data interfaces
export interface PromptGeneratorRequestedData {
	domain: string;
	frameworks: string[];
	includePersonas: boolean;
	hasLocation: boolean;
}

export interface PromptGeneratorCompletedData {
	generationId: string;
	promptCount: number;
	personaCount: number;
	includePersonas: boolean;
	/** True when the result was restored from a `?generation=` link. */
	restored: boolean;
}

export interface PromptGeneratorFailedData {
	generationId?: string;
	/** "start": the request was refused; "generation": the job failed. */
	stage: "start" | "generation";
	errorMessage: string;
}

export interface PromptGeneratorPersonasViewedData {
	generationId: string;
	personaCount: number;
}

/** The generation only. Never the address. */
export interface PromptGeneratorEmailSubmittedData {
	generationId: string;
}

export interface PromptGeneratorExportClickedData {
	generationId: string;
	format: "csv" | "clipboard";
	promptCount: number;
	personaCount: number;
}

export interface PromptGeneratorTrackCtaClickedData {
	generationId: string;
	promptCount: number;
	personaCount: number;
}

export interface PromptGeneratorClaimCompletedData {
	generationId: string;
	propertyId: string;
	importedPromptCount: number;
	importedPersonaCount: number;
	projectCreated: boolean;
}

// Go-to-market conversion data. Never an email address or other personal
// data: LinkedIn receives only the conversion id, and PostHog only these.
export interface GtmReportPreviewRunData {
	reportId: string;
	subjectType: "brand" | "person";
}

export interface GtmReportPurchasedData {
	reportId: string;
}

export interface GtmSignupCompletedData {
	/** Minutes from account creation to the first app visit that fired this. */
	accountAgeMinutes: number;
}

export interface GtmDemoBookedData {
	source: "demo_page";
}

/** The data each go-to-market conversion takes, keyed by event name. */
export interface GtmConversionDataMap {
	[GTM_CONVERSION_EVENTS.GTM_REPORT_PREVIEW_RUN]: GtmReportPreviewRunData;
	[GTM_CONVERSION_EVENTS.GTM_REPORT_PURCHASED]: GtmReportPurchasedData;
	[GTM_CONVERSION_EVENTS.GTM_SIGNUP_COMPLETED]: GtmSignupCompletedData;
	[GTM_CONVERSION_EVENTS.GTM_DEMO_BOOKED]: GtmDemoBookedData;
}

// Union type for all event data
export type EventData =
	| UserSignedUpData
	| EmailVerifiedData
	| TrialStartedData
	| PricingClickedData
	| ContactFormSubmittedData
	| CTAClickedData
	| PropertyVerifiedData
	| PurchaseInitiatedData
	| PurchaseCompletedData
	| SubscriptionEventData
	| SetupWorkflowStartedData
	| SetupTrackingReadyData
	| AIReportPageViewedData
	| AIReportGenerationRequestedData
	| AIReportGenerationCompletedData
	| AIReportGenerationFailedData
	| AIReportViewedData
	| AIReportUnlockButtonClickedData
	| AIReportPurchaseInitiatedData
	| AIReportPurchasedData
	| AIReportSharedData
	| AIReportTourStartedData
	| AIReportTourSlideViewedData
	| AIReportTourNavigatedData
	| AIReportTourCompletedData
	| AIReportTourClosedData
	| AIReportTourCTAClickedData
	| PromptGeneratorRequestedData
	| PromptGeneratorCompletedData
	| PromptGeneratorFailedData
	| PromptGeneratorPersonasViewedData
	| PromptGeneratorEmailSubmittedData
	| PromptGeneratorExportClickedData
	| PromptGeneratorTrackCtaClickedData
	| PromptGeneratorClaimCompletedData
	| GtmReportPreviewRunData
	| GtmReportPurchasedData
	| GtmSignupCompletedData
	| GtmDemoBookedData;

// All available events
export const ALL_EVENTS = {
	...CONVERSION_EVENTS,
	...MARKETING_EVENTS,
	...PRODUCT_EVENTS,
	...SETUP_EVENTS,
	...AI_REPORT_EVENTS,
	...PROMPT_GENERATOR_EVENTS,
	...GTM_CONVERSION_EVENTS,
} as const;

export type EventName = (typeof ALL_EVENTS)[keyof typeof ALL_EVENTS];
